import type { CSSProperties } from "react";
import {
  CONTROL_LIT,
  PEWTER_GAP,
  PEWTER_OUTLINE,
  pewterEdge,
  solid,
} from "@majourneys/shared/ui";

/**
 * The pewter band comes from `@majourneys/shared/ui`, the one place that
 * defines it.
 *
 * This file used to carry its own copy of the recipe — the 155° gradient, its
 * five stops, the 4px width, the white base underneath. That was written when
 * the `./ui` subpath only existed on an unmerged branch, so importing it would
 * typecheck in a working copy that happened to have the branch and fail in a
 * clean one: a build that breaks on the deploy and nowhere earlier. The
 * subpath is on master now, so the copy is gone and the two cannot drift.
 *
 * The band is 4px of white with a part-transparent gradient painted on top of
 * it. White has to be underneath: the gradient's own stops let light through,
 * so without it the band takes the colour of whatever is behind the panel and
 * stops reading as white at all — which is what `base` is for.
 */

/**
 * The house colours and materials a menu is made of.
 *
 * Written as plain values and inline style objects rather than Tailwind
 * classes on purpose. The app's Tailwind build only scans `apps/majourneys/src`
 * and `packages/booking/src`, so a class written inside a package is emitted
 * only when the app happens to use the same string somewhere else — which is a
 * style that works until the day the app stops using it. Inline styles are
 * always there, in every consumer, with no build to keep in step.
 */

export const NAVY = "#110E39";
export const GOLD = "#E5B265";
/** Gold dark enough to read as text on white. */
export const GOLD_INK = "#8A6425";
export const GOLD_WASH = "rgba(229,178,101,0.16)";
/** Body meta text. Anything lighter fails AA on small type. */
export const MUTED = "#5a5d77";
export const PLACEHOLDER = "#9a9bb5";
export const HAIRLINE = "rgba(17,14,57,0.10)";
export const RED = "#9B2C2C";

/**
 * A control at rest: lit white, one navy hairline, the hairline darkening when
 * the menu is open or a choice has been made. The gradient is the FILL; the
 * pewter band — a gradient border — belongs to the panel that opens, never to
 * the thing you press.
 */
export const CONTROL_EDGE = "rgba(17,14,57,0.16)";
export const CONTROL_EDGE_ON = "rgba(17,14,57,0.55)";
/** Lit white, from `@majourneys/shared/ui` — one fill for every control. */
export const CONTROL_FILL = CONTROL_LIT;

/** The wash under the row the pointer or the arrow keys is sitting on. */
export const ROW_HOVER = "rgba(17,14,57,0.05)";
export const ROW_ACTIVE = "rgba(17,14,57,0.08)";
/** The quieter wash on the row that is already the current one. */
export const ROW_CURRENT = "rgba(17,14,57,0.045)";

const PANEL_FILL =
  "linear-gradient(180deg, rgba(252,252,254,1) 0%, rgba(246,246,251,1) 62%, rgba(242,242,248,1) 100%)";

const FLOAT_SHADOW = "0 18px 44px -18px rgba(17,14,57,0.45)";

/**
 * The house frame on a panel that hangs over the page: a white band with the
 * pewter gradient painted on it, the panel's own hairline inside, and a float
 * shadow under the whole thing.
 *
 * Built from the shared band rather than from the app's `card-frame.ts`,
 * because a package cannot import from an app and a menu is not a card. Same
 * recipe, same numbers — now literally the same numbers.
 */
export function menuPanelStyle(radius = 18): CSSProperties {
  return {
    ...pewterEdge(PANEL_FILL, radius, {
      style: "pewter",
      width: PEWTER_GAP,
      base: solid("rgba(255,255,255,1)"),
    }),
    boxShadow: [
      `0 0 0 1px ${PEWTER_OUTLINE}`,
      FLOAT_SHADOW,
      "inset 0 0 0 1px rgba(17,14,57,0.13)",
    ].join(", "),
  } as CSSProperties;
}

/**
 * A phone sheet floats: 10px in from both sides and lifted 10px off the bottom
 * above the safe area, rounded on all four corners, so the band shows whole and
 * never runs into the screen's edge (owner, 2026-10-11, lab item 127). The same
 * gap the booking sheet has always carried (`SHEET_INSET` in booking-sheet.tsx).
 */
export const SHEET_INSET = 10;

/** The sheet's lift off the bottom edge: the safe area plus the side gap. */
export const SHEET_BOTTOM = `calc(env(safe-area-inset-bottom, 0px) + ${SHEET_INSET}px)`;

/**
 * How far a floating sheet travels to leave the screen: its own height plus
 * the lift, so no sliver of the band is left above the bottom edge.
 */
export const SHEET_OFFSCREEN = `translateY(calc(100% + ${SHEET_BOTTOM}))`;

/** The same frame on the bottom sheet. It floats, so every corner is round. */
export function sheetPanelStyle(radius = 26): CSSProperties {
  return menuPanelStyle(radius);
}

/** The trigger's own box: white, one hairline, darker when on. */
export function controlStyle(on: boolean): CSSProperties {
  return {
    color: NAVY,
    background: CONTROL_FILL,
    boxShadow: `0 0 0 1.5px ${on ? CONTROL_EDGE_ON : CONTROL_EDGE}`,
    transition: "box-shadow 160ms cubic-bezier(0.23, 1, 0.32, 1)",
  };
}
